<template>
  <div>
    <!-- Indicadores só dos leitores que falam com alguma suite; clicar filtra a tabela. -->
    <v-row v-if="temSuite" class="mt-1 mb-1" dense>
      <v-col v-for="card in kpiCards" :key="card.key" cols="6" sm="4" md="2" class="d-flex">
        <v-card class="pa-2 flex-grow-1 kpi-card" :outlined="filtroKpi !== card.key"
          :color="filtroKpi === card.key ? 'primary lighten-5' : undefined" @click="alternaKpi(card.key)">
          <div class="text-caption grey--text text--darken-1">{{ card.label }}</div>
          <div class="text-h6 font-weight-bold" :style="{ color: card.color }">{{ card.value }}</div>
        </v-card>
      </v-col>
    </v-row>

    <merus-data-table
      :key="tableKey"
      ref="tabela"
      cod-tabela="EQUIPAMENTOS__VALIDADORES"
      :headers="headers"
      :items="itensFiltrados"
      :mostrar-inativos="mostrarInativos"
      :loading="loading"
      :default-multi-sort="defaultSort"
      :loading-text="$t('equipment.loading')"
      item-key="id_equipamento"
      show-select
      v-model="selecionados"
      @update:headers="headers = $event"
      @update:displayedItems="displayedItemsForExport = $event"
    >
      <template v-slot:toolbar>
        <CadastroToolbar
          :title="$t('equipment.panel.tabs.validators')"
          :token="token"
          :items="displayedItemsForExport !== null ? displayedItemsForExport : itensFiltrados"
          :csv-title="$t('equipment.panel.tabs.validators')"
          :pdf-title="$t('equipment.panel.tabs.validators')"
          :pdf-headers="headers"
          :pdf-filename="$t('equipment.panel.tabs.validators')"
          :endpoint-upload-csv="permissoes.editar || permissoes.inserir ? '/api/v1.0/processacsvequipamentos/' : null"
          :csv-headers="headersCsv"
          :help-dialog-csv-fields="helpCsv"
          :help-dialog-csv-model="$tc('equipment.self', 2)"
          hide-search
          @saveBulk="$emit('saveBulk', $event)"
        >
          <template v-slot:extension>
            <v-switch
              :input-value="mostrarInativos"
              :label="$t('crudVeiculos.showInactive')"
              class="ml-3 mr-4"
              hide-details
              @change="$emit('update:mostrarInativos', !!$event)"
            ></v-switch>
            <v-btn v-if="permissoes.editar" color="primary" dark small class="mb-2 mr-2"
              :disabled="!selecionados.length" @click="$emit('bulk-edit', selecionados)">
              <v-icon left small>mdi-pencil</v-icon>
              {{ $t('equipment.bulkEdit') }}
            </v-btn>
          </template>
          <template v-slot:add>
            <v-tooltip top>
              <template v-slot:activator="{ on }">
                <v-btn v-on="on" fab small color="primary" dark class="mb-2 mr-2" :disabled="enfileirando" @click="$emit('refresh')">
                  <v-icon>mdi-refresh</v-icon>
                </v-btn>
              </template>
              <span>{{ $t('statusDispositivos.actions.refresh') }}</span>
            </v-tooltip>
            <!-- Ações de frota: só onde há leitor que fala com alguma suite e quem pode disparar. -->
            <template v-if="permissoes.sincronizar && temSuite">
              <v-tooltip v-if="algumaSuiteInventaria" top max-width="360">
                <template v-slot:activator="{ on }">
                  <v-btn v-on="on" fab small color="primary" dark class="mb-2 mr-2" :disabled="enfileirando" @click="pedeConfirmacaoFrota('inventory')">
                    <v-icon>mdi-database-refresh</v-icon>
                  </v-btn>
                </template>
                <span>{{ $t('statusDispositivos.actions.inventoryFleet') }} — {{ $t('equipment.help.inventory') }}</span>
              </v-tooltip>
              <v-tooltip top max-width="360">
                <template v-slot:activator="{ on }">
                  <v-btn v-on="on" fab small color="primary" dark class="mb-2 mr-2" :disabled="enfileirando" @click="pedeConfirmacaoFrota('apply')">
                    <v-icon>mdi-sync</v-icon>
                  </v-btn>
                </template>
                <span>{{ $t('statusDispositivos.actions.syncFleet') }} — {{ $t('equipment.help.sync') }}</span>
              </v-tooltip>
            </template>
            <template v-if="permissoes.inserir">
              <v-tooltip top>
                <template v-slot:activator="{ on }">
                  <v-btn v-on="on" color="primary" dark small class="mb-2 mr-2" fab @click="$emit('qr-download')">
                    <v-icon>mdi-qrcode</v-icon>
                  </v-btn>
                </template>
                <span>{{ $t('equipment.qrcode.downloadTitle') }}</span>
              </v-tooltip>
              <v-tooltip top>
                <template v-slot:activator="{ on }">
                  <v-btn v-on="on" color="primary" dark small class="mb-2 mr-2" fab @click="$emit('novo')">
                    <v-icon>mdi-plus</v-icon>
                  </v-btn>
                </template>
                <span>{{ $t('equipment.registerEquipment') }}</span>
              </v-tooltip>
            </template>
          </template>
        </CadastroToolbar>
      </template>

      <template v-slot:item.suite_extenso="{ item }">
        <span v-if="item.suite_extenso">
          {{ item.suite_extenso }}
          <span v-if="item.suite_definida_no_equipamento == 0" class="text-caption grey--text">
            ({{ $t('equipment.headers.main.suiteDefaultTag') }})
          </span>
        </span>
      </template>

      <template v-slot:item.status_comunicacao_extenso="{ item }">
        <v-icon small :color="corComunicacao(item.status)">{{ iconeComunicacao(item.status) }}</v-icon>
        {{ item.status_comunicacao_extenso }}
      </template>

      <template v-slot:item.resultado_sincronizacao_extenso="{ item }">
        <v-chip v-if="item.fala_com_suite" small dark :color="corResultadoSincronizacao(item.status_ultima_sincronizacao)">
          {{ item.resultado_sincronizacao_extenso }}
        </v-chip>
      </template>

      <!-- Mensagem de erro pode ser longa (resposta da API embutida): uma linha, completa no tooltip. -->
      <template v-slot:item.msg_ultima_sincronizacao_extenso="{ item }">
        <v-tooltip v-if="item.msg_ultima_sincronizacao" bottom max-width="600">
          <template v-slot:activator="{ on, attrs }">
            <span class="texto-uma-linha" v-bind="attrs" v-on="on">{{ item.msg_ultima_sincronizacao_extenso }}</span>
          </template>
          <span>{{ item.msg_ultima_sincronizacao }}</span>
        </v-tooltip>
        <span v-else>{{ item.msg_ultima_sincronizacao_extenso }}</span>
      </template>

      <template v-slot:item.firmware_permitido_extenso="{ item }">
        <v-chip v-if="item.firmware_permitido_extenso" small dark :color="item.firmware_permitido == 1 ? 'green' : 'red'">
          {{ item.firmware_permitido_extenso }}
        </v-chip>
      </template>

      <!-- A mesma coluna mistura aparelho (ISAPI) e nível de acesso (HikCentral): o ícone diz qual. -->
      <template v-slot:item.qtd_pessoas_extenso="{ item }">
        <span>{{ item.qtd_pessoas_extenso }}</span>
        <v-tooltip v-if="item.fonte_inventario" top>
          <template v-slot:activator="{ on }">
            <v-icon v-on="on" x-small class="ml-1" color="grey darken-1">
              {{ item.fonte_inventario === 'NIVEL_ACESSO' ? 'mdi-shield-account-outline' : 'mdi-cellphone-text' }}
            </v-icon>
          </template>
          <span>{{ item.fonte_inventario === 'NIVEL_ACESSO'
            ? $t('statusDispositivos.inventory.countFromAccessLevel')
            : $t('statusDispositivos.inventory.countFromDevice') }}</span>
        </v-tooltip>
      </template>

      <template v-slot:item.qtd_erros_extenso="{ item }">
        <v-tooltip v-if="item.qtd_erros_total > 0" top>
          <template v-slot:activator="{ on }">
            <span v-on="on" :class="item.qtd_erros_esgotados > 0 ? 'red--text' : 'orange--text text--darken-3'">
              {{ item.qtd_erros_extenso }}
            </span>
          </template>
          <span>{{ tooltipErros(item) }}</span>
        </v-tooltip>
        <span v-else>{{ item.qtd_erros_extenso }}</span>
      </template>

      <template v-slot:item.actions="{ item }">
        <div class="d-flex align-center flex-nowrap">
          <template v-if="permissoes.sincronizar">
            <v-tooltip top max-width="360">
              <template v-slot:activator="{ on }">
                <span v-on="on">
                  <v-btn icon small color="primary" :disabled="!item.suporta_inventario || enfileirando" @click="dispara(item, 'inventory')">
                    <v-icon small>mdi-database-refresh</v-icon>
                  </v-btn>
                </span>
              </template>
              <span>{{ tooltipInventario(item) }}</span>
            </v-tooltip>
            <v-tooltip top max-width="360">
              <template v-slot:activator="{ on }">
                <span v-on="on">
                  <v-btn icon small color="primary" :disabled="!item.suporta_aplicar_escala || enfileirando" @click="dispara(item, 'apply')">
                    <v-icon small>mdi-sync</v-icon>
                  </v-btn>
                </span>
              </template>
              <span>{{ tooltipSync(item) }}</span>
            </v-tooltip>
          </template>
          <v-icon small class="mx-1" color="primary" :disabled="!permissoes.editar" @click="$emit('status', item)">mdi-progress-check</v-icon>
          <v-icon small class="mx-1" color="green" :disabled="!permissoes.editar" @click="$emit('editar', item)">mdi-pencil</v-icon>
          <v-icon small class="mx-1" color="red" :disabled="!permissoes.excluir" @click="$emit('excluir', item)">mdi-delete</v-icon>
        </div>
      </template>
    </merus-data-table>

    <v-dialog v-model="confirmacao.aberta" persistent max-width="520">
      <v-card>
        <v-card-title class="text-subtitle-1">{{ confirmacao.mensagem }}</v-card-title>
        <v-card-text class="text-caption">{{ confirmacao.ajuda }}</v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="confirmacao.aberta = false">{{ $t('button.cancel') }}</v-btn>
          <v-btn color="primary" text :loading="enfileirando" @click="confirmaDisparoFrota">
            {{ $t('button.confirm') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import apiController from '@/utils/apiController';
import EventBus from '@/utils/eventBus';
import MerusDataTable from '../../Tables/MerusDataTable.vue';
import CadastroToolbar from '../../Toolbars/CadastroToolbar.vue';
import dispositivosFormatters from './dispositivosFormatters';

/**
 * Validadores: o cadastro e o estado de envio de cada leitor na mesma linha (antes eram as
 * telas de Equipamentos e Status dos Dispositivos). As linhas vêm normalizadas do painel
 * (fala_com_suite, suporta_inventario, fonte_inventario...); leitor sem suite é só cadastro.
 * Gravações do cadastro sobem para o CRUDEquipamentos; disparos da suite ficam aqui.
 */
export default {
  name: 'TabelaValidadores',
  components: { MerusDataTable, CadastroToolbar },
  mixins: [dispositivosFormatters],
  props: {
    itens: { type: Array, default: () => [] },
    loading: { type: Boolean, default: false },
    token: { type: String, default: null },
    mostrarInativos: { type: Boolean, default: false },
    permissoes: { type: Object, default: () => ({}) },
    headersCsv: { type: Array, default: () => [] },
    helpCsv: { type: Array, default: () => [] },
  },
  data: () => ({
    headers: [],
    selecionados: [],
    displayedItemsForExport: null,
    filtroKpi: null,
    enfileirando: false,
    pollTimer: null,
    pollInFlight: false,
    confirmacao: { aberta: false, mensagem: '', ajuda: '', modo: null },
  }),
  computed: {
    tableKey() {
      return `equipamentos-validadores-${this.$i18n.locale}`;
    },
    defaultSort() {
      return [{ value: 'nome_equipamento', options: { text: this.$t('routs.increasing'), value: 1 } }];
    },
    comSuite() {
      return this.itens.filter((e) => e.fala_com_suite);
    },
    temSuite() {
      return this.comSuite.length > 0;
    },
    algumaSuiteInventaria() {
      return this.comSuite.some((e) => e.suporta_inventario);
    },
    // Uma linha por leitor, na linguagem da tela: o que a suite não fornece vira traço.
    itensExtenso() {
      return this.itens.map((e) => {
        const erros = (e.qtd_erros_pendentes || 0) + (e.qtd_erros_esgotados || 0) + (e.qtd_erros_hikcentral || 0);
        return {
          ...e,
          suite_extenso: e.nome_suite_efetiva || e.suite_efetiva || '',
          status_comunicacao_extenso: this.fmtStatusComunicacao(e.status),
          dt_ultima_comunicacao_extenso: this.fmtDataHora(e.dt_ultima_atualizacao_comunicacao),
          dt_ultima_sincronizacao_extenso: e.fala_com_suite ? this.fmtDataHora(e.dt_ultima_sincronizacao) : '',
          resultado_sincronizacao_extenso: e.fala_com_suite ? this.fmtResultadoSincronizacao(e.status_ultima_sincronizacao) : '',
          msg_ultima_sincronizacao_extenso: e.fala_com_suite ? this.fmtTexto(e.msg_ultima_sincronizacao) : '',
          qtd_pessoas_extenso: e.suporta_inventario ? this.fmtNumero(e.qtd_pessoas) : '',
          qtd_cartoes_extenso: e.suporta_inventario && e.fonte_inventario !== 'NIVEL_ACESSO' ? this.fmtNumero(e.qtd_cartoes) : '',
          qtd_erros_total: erros,
          qtd_erros_extenso: e.fala_com_suite ? String(erros) : '',
          nivel_acesso_extenso: e.suite_efetiva === 'HIKCENTRAL' ? this.fmtTexto(e.nivel_acesso_extenso) : '',
          ip_isapi_efetivo: e.suite_efetiva === 'ISAPI' ? this.fmtTexto(e.ip_isapi_efetivo) : '',
          // A lista de firmwares só filtra o ISAPI: nas outras suites a coluna fica vazia.
          firmware_version_efetivo: e.suite_efetiva === 'ISAPI' ? this.fmtTexto(e.firmware_version_efetivo) : '',
          firmware_released_date_efetivo: e.suite_efetiva === 'ISAPI' ? this.fmtTexto(e.firmware_released_date_efetivo) : '',
          firmware_permitido_extenso: e.suite_efetiva === 'ISAPI' ? this.fmtSimNao(e.firmware_permitido) : '',
        };
      });
    },
    itensFiltrados() {
      const filtro = this.kpiFiltros[this.filtroKpi];
      return filtro ? this.itensExtenso.filter(filtro) : this.itensExtenso;
    },
    kpiFiltros() {
      return {
        online: (e) => e.fala_com_suite && e.status == 1,
        offline: (e) => e.fala_com_suite && e.status == 2,
        syncOk: (e) => e.fala_com_suite && e.status_ultima_sincronizacao === 'ok',
        syncError: (e) => e.fala_com_suite && e.status_ultima_sincronizacao === 'erro',
        neverSynced: (e) => e.fala_com_suite && !e.status_ultima_sincronizacao,
        withErrors: (e) => e.fala_com_suite && e.qtd_erros_total > 0,
      };
    },
    kpiCards() {
      const lista = this.itensExtenso;
      const conta = (chave) => lista.filter(this.kpiFiltros[chave]).length;
      return [
        { key: 'online', label: this.$t('statusDispositivos.kpis.online'), value: conta('online'), color: '#4caf50' },
        { key: 'offline', label: this.$t('statusDispositivos.kpis.offline'), value: conta('offline'), color: '#f44336' },
        { key: 'syncOk', label: this.$t('statusDispositivos.kpis.syncOk'), value: conta('syncOk'), color: '#2e7d32' },
        { key: 'syncError', label: this.$t('statusDispositivos.kpis.syncError'), value: conta('syncError'), color: '#c62828' },
        { key: 'neverSynced', label: this.$t('statusDispositivos.kpis.neverSynced'), value: conta('neverSynced'), color: '#546e7a' },
        { key: 'withErrors', label: this.$t('equipment.panel.kpis.withErrors'), value: conta('withErrors'), color: '#ef6c00' },
      ];
    },
  },
  watch: {
    '$i18n.locale'() {
      this.initializeHeaders();
    },
  },
  created() {
    this.initializeHeaders();
  },
  beforeDestroy() {
    this.limpaPoll();
  },
  methods: {
    limpaSelecao() {
      this.selecionados = [];
    },
    alternaKpi(chave) {
      this.filtroKpi = this.filtroKpi === chave ? null : chave;
    },
    // Lista padrão; a configuração da tabela (EQUIPAMENTOS__VALIDADORES) substitui quando existir.
    initializeHeaders() {
      const h = this.$t.bind(this);
      this.headers = [
        { text: h('statusDispositivos.headers.equipment'), value: 'nome_equipamento', sortable: true, filtrable: true },
        { text: h('statusDispositivos.headers.equipmentCode'), value: 'cod_equipamento', sortable: true, filtrable: true },
        { text: h('statusDispositivos.headers.vehicle'), value: 'cod_veiculo', sortable: true, filtrable: true },
        { text: h('equipment.headers.main.equipmentType'), value: 'nome_tipo_equipamento', sortable: true, filtrable: true },
        { text: h('statusDispositivos.headers.suite'), value: 'suite_extenso', sortable: true, filtrable: true },
        { text: h('statusDispositivos.headers.commStatus'), value: 'status_comunicacao_extenso', sortable: true, filtrable: true },
        { text: h('statusDispositivos.headers.lastComm'), value: 'dt_ultima_comunicacao_extenso', sortable: true, filtrable: true },
        { text: h('statusDispositivos.headers.lastSync'), value: 'dt_ultima_sincronizacao_extenso', sortable: true, filtrable: true },
        { text: h('statusDispositivos.headers.syncResult'), value: 'resultado_sincronizacao_extenso', sortable: true, filtrable: true },
        { text: h('statusDispositivos.headers.syncMessage'), value: 'msg_ultima_sincronizacao_extenso', sortable: true, filtrable: true },
        { text: h('statusDispositivos.headers.peopleCount'), value: 'qtd_pessoas_extenso', sortable: true, filtrable: true },
        { text: h('statusDispositivos.headers.cardsCount'), value: 'qtd_cartoes_extenso', sortable: true, filtrable: true },
        { text: h('equipment.panel.headers.sendErrors'), value: 'qtd_erros_extenso', sortable: true, filtrable: true },
        { text: h('equipment.headers.main.integrationLevelAccess'), value: 'nivel_acesso_extenso', sortable: true, filtrable: true },
        { text: h('equipment.headers.main.isapiAddress'), value: 'ip_isapi_efetivo', sortable: true, filtrable: true },
        { text: h('equipment.headers.main.firmwareVersion'), value: 'firmware_version_efetivo', sortable: true, filtrable: true },
        { text: h('equipment.headers.main.firmwareReleasedDate'), value: 'firmware_released_date_efetivo', sortable: true, filtrable: true },
        { text: h('equipment.headers.main.firmwareAllowed'), value: 'firmware_permitido_extenso', sortable: true, filtrable: true },
        { text: h('equipment.headers.main.status'), value: 'descricao_status_equipamento', sortable: true, filtrable: true },
        { text: h('equipment.headers.main.active'), value: 'ativo_extenso', sortable: true, filtrable: true },
      ];
    },
    tooltipErros(item) {
      const partes = [];
      if (item.qtd_erros_pendentes) partes.push(`${this.$t('statusDispositivos.headers.pendingErrors')}: ${item.qtd_erros_pendentes}`);
      if (item.qtd_erros_esgotados) partes.push(`${this.$t('statusDispositivos.headers.exhaustedErrors')}: ${item.qtd_erros_esgotados}`);
      if (item.qtd_erros_hikcentral) partes.push(`${this.$t('equipment.panel.headers.hikcentralErrors')}: ${item.qtd_erros_hikcentral}`);
      return `${partes.join(' · ')} — ${this.$t('equipment.help.sendErrorsColumn')}`;
    },
    tooltipInventario(item) {
      return item.suporta_inventario
        ? `${this.$t('statusDispositivos.actions.inventory')} — ${this.$t('equipment.help.inventory')}`
        : this.$t('statusDispositivos.messages.suiteWithoutInventory');
    },
    tooltipSync(item) {
      return item.suporta_aplicar_escala
        ? `${this.$t('statusDispositivos.actions.sync')} — ${this.$t('equipment.help.sync')}`
        : this.$t('statusDispositivos.messages.suiteWithoutSync');
    },
    dispara(item, modo) {
      this.enfileira({ modo, id_equipamento: item.id_equipamento });
    },
    pedeConfirmacaoFrota(modo) {
      this.confirmacao = {
        aberta: true,
        modo,
        mensagem: modo === 'inventory'
          ? this.$t('statusDispositivos.messages.confirmInventoryFleet')
          : this.$t('statusDispositivos.messages.confirmSyncFleet'),
        ajuda: modo === 'inventory' ? this.$t('equipment.help.inventory') : this.$t('equipment.help.sync'),
      };
    },
    confirmaDisparoFrota() {
      const modo = this.confirmacao.modo;
      this.confirmacao.aberta = false;
      this.enfileira({ modo });
    },
    enfileira(payload) {
      this.enfileirando = true;
      apiController.request(apiController.endpoints.isapiForcasyncisapi, {
        data: payload,
        onSuccess: (response) => {
          const dados = response.data || {};
          EventBus.$emit('show-success', { message: this.$t('statusDispositivos.messages.queued') });
          if (dados.id_processo) {
            this.acompanhaProcesso(dados.id_processo);
          } else {
            this.enfileirando = false;
          }
        },
        onError: () => {
          this.enfileirando = false;
        },
      }).catch(() => {});
    },
    limpaPoll() {
      if (this.pollTimer != null) {
        clearInterval(this.pollTimer);
        this.pollTimer = null;
      }
      this.pollInFlight = false;
    },
    // O disparo manual roda no scheduler; a tela acompanha até DONE/FAILED e então recarrega o
    // estado, para o usuário ver o resultado sem apertar "atualizar".
    acompanhaProcesso(idProcesso, waitMs = 15 * 60 * 1000, pollMs = 5000) {
      this.limpaPoll();
      const limite = Date.now() + waitMs;
      const encerra = (evento, carga) => {
        this.limpaPoll();
        this.enfileirando = false;
        if (evento) EventBus.$emit(evento, carga);
        this.$emit('refresh');
      };

      const consulta = () => {
        if (Date.now() > limite) {
          encerra('show-error', { error: this.$t('statusDispositivos.messages.processTimeout') });
          return;
        }
        if (this.pollInFlight) return;
        this.pollInFlight = true;

        apiController.request(apiController.endpoints.schedulerGetProcessoschedulerbyid, {
          data: { id_processo: idProcesso },
          silent: true,
          onSuccess: (res) => {
            this.pollInFlight = false;
            const dados = res.data || {};
            const status = dados.processo && dados.processo.status;
            if (status === 'PENDING' || status === 'EXECUTING') return;
            if (status === 'DONE') {
              encerra('show-success', { message: this.$t('statusDispositivos.messages.processDone') });
              return;
            }
            encerra('show-error', {
              error: (dados.processo && dados.processo.mensagem_scheduler)
                || this.$t('statusDispositivos.messages.processFailed'),
            });
          },
          onError: () => {
            this.pollInFlight = false;
            encerra('show-error', { error: this.$t('statusDispositivos.messages.processFailed') });
          },
        }).catch(() => {});
      };

      this.pollTimer = setInterval(consulta, pollMs);
      consulta();
    },
  },
};

</script>

<style scoped>
.texto-uma-linha {
  display: inline-block;
  max-width: 280px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  vertical-align: middle;
}
.kpi-card {
  border-radius: 10px;
  cursor: pointer;
}
</style>
